import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Carousel } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiArrowBack, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { LoaderContext, RegulationContext, SidebarContext } from '../../../App';
import { CompareCategoryType, DataViewCategoryType, LS_REGULATION_SELECTED_KEY } from '../../../core/constants/common';
import CompanyDashboardService from '../../../core/service/CompanyDashboardService';
import {
  collectStackedBarCarouselItemsFromDataView,
  getConfigRowForStackedBarCarouselItem,
} from '../../../core/utilities/esgAnalyticsStackedBarCarousel';
import { getAnalyticsFiltersTotalCount } from '../../../core/utilities/esgAnalyticsFilters';
import '../../Common/Dataview/DataView.scss';
import './ESGAnalytics.scss';
import ESGAnalyticsCards from './ESGAnalyticsCards';
import ESGAnalyticsLineGraphYearly from './ESGAnalyticsLineGraphYearly';
import ESGAnalyticsStackedBarChart from './ESGAnalyticsStackedBarChart';
import ESGAnalyticsSummaryCard from './ESGAnalyticsSummaryCard';
import ESGUnitWiseConfigForm from './ESGUnitWiseConfigForm';

const ESGAnalytics = ({ t }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { setSidebarDisplay } = useContext(SidebarContext);
  const glbRegulationId = glbRegulation?.id ?? localStorage.getItem(LS_REGULATION_SELECTED_KEY) ?? "";

  const [filter, setFilter] = useState({
    standardId: glbRegulationId,
    periodType: 'YEARLY',
    period: null,
    category: 'ENVIRONMENTAL',
    categoryType: DataViewCategoryType.ENVIRONMENTAL,
    topicIds: [],
    metricIds: [],
    datapointIds: [],
    parentBranchIds: [],
    childBranchIds: [],
    businessUnitIds: [],
    childEntities: [],
    parentEntities: [],
    selectedRepPeriod: [],
    nullInfoConfig: [],
  });

  // Filter that was used when data was last fetched (Apply clicked). Chart and cards use this so they only update on Apply.
  const [appliedFilter, setAppliedFilter] = useState(null);

  const [dataViewData, setDataViewData] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [dataPoints, setDataPoints] = useState({});
  const [yearData, setYearData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [configRows, setConfigRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [hasSummaryData, setHasSummaryData] = useState(true);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryItemsPerPage, setSummaryItemsPerPage] = useState(10);
  const [summaryTotalCount, setSummaryTotalCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [filterId, setFilterId] = useState(null);
  const [title, setTitle] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  // When true, config form shows only + button (used when opened via Edit button). When false, show full form (New, View, card click).
  const [simplifiedEditForm, setSimplifiedEditForm] = useState(false);
  // When true, show required validation and red borders on Add Widgets form; Keep Save button active.
  const [showValidationOnForm, setShowValidationOnForm] = useState(false);

  let sortOrder = [];

  const fetchAnalyticsFilters = useCallback((pageNumber = 1, pageSize = 10) => {
    if (!glbRegulationId) return;

    const apiPageNumber = pageNumber > 0 ? pageNumber - 1 : 0;

    setLoaderDisplay(true);
    CompanyDashboardService.getAnalyticsFilters(glbRegulationId, 'CONFIGURATION', apiPageNumber, pageSize)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status === 200 && res.data) {
          const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data.content || []);
          const totalCount = getAnalyticsFiltersTotalCount(res, data.length);
          setSummaryTotalCount(totalCount);

          if (data.length === 0 && pageNumber > 1 && totalCount > 0) {
            setSummaryPage(pageNumber - 1);
            return;
          }

          if (data && Array.isArray(data) && data.length > 0) {
            setSummaryData(data);
            setHasSummaryData(true);
            setEditMode(false);
            setSimplifiedEditForm(false);
          } else {
            setSummaryData(null);
            setHasSummaryData(false);
            setEditMode(true);
          }
        } else {
          setSummaryTotalCount(0);
          setSummaryData(null);
          setHasSummaryData(false);
          setEditMode(true);
        }
      })
      .catch(() => {
        setLoaderDisplay(false);
        setSummaryTotalCount(0);
        setSummaryData(null);
        setHasSummaryData(false);
        setEditMode(true);
      });
  }, [glbRegulationId, setLoaderDisplay]);

  // Fetch analytics filters/summary data on component mount and when pagination changes
  useEffect(() => {
    setSummaryPage(1);
  }, [glbRegulationId]);

  useEffect(() => {
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  }, [fetchAnalyticsFilters, summaryPage, summaryItemsPerPage]);

  const handleSummaryPageChange = (pageNum, perPage) => {
    if (perPage) {
      setSummaryItemsPerPage(perPage);
      setSummaryPage(1);
      return;
    }
    if (pageNum > 0) {
      setSummaryPage(pageNum);
    }
  };

  // When hasSummaryData is false and there's no filterId, we're creating a new configuration
  // So default to edit mode to show all filters and config form
  useEffect(() => {
    if (!hasSummaryData && !filterId) {
      setEditMode(true);
    }
  }, [hasSummaryData, filterId]);

  // Collapse main sidebar when in view mode, restore when in edit mode or summary view
  useEffect(() => {
    if (!hasSummaryData && !editMode) {
      // View mode: collapse main sidebar
      setSidebarDisplay(false);
    } else {
      // Edit mode or summary view: restore main sidebar
      setSidebarDisplay(true);
    }
  }, [hasSummaryData, editMode, setSidebarDisplay]);

  // Cleanup: restore sidebar when component unmounts
  useEffect(() => {
    return () => {
      setSidebarDisplay(true);
    };
  }, [setSidebarDisplay]);

  // Handle scroll detection for sticky sidebar (only in view mode)
  useEffect(() => {
    if (editMode || hasSummaryData) return; // Only apply in view mode

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial scroll position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [editMode, hasSummaryData]);

  const handleNewClick = () => {
    setSimplifiedEditForm(false); // New: show full form (Level 1 + Level 2 sections)
    setShowValidationOnForm(true); // Show required validation and red borders; Keep Save active
    setHasSummaryData(false);
    setSummaryData(null);
    setEditMode(true); // Set to true to show all filters and allow editing
    setFilterId(null);
    setConfigRows([]); // Reset config rows for new configuration
    setTitle(''); // Reset title for new configuration
    setAppliedFilter(null);
    setDataViewData([]);
    setBranches([]);
    setYearData([]);
    // Reset filter to default so category dropdown shows Environmental for new widget
    setFilter({
      standardId: glbRegulationId,
      periodType: 'YEARLY',
      period: null,
      category: 'ENVIRONMENTAL',
      categoryType: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      metricIds: [],
      datapointIds: [],
      parentBranchIds: [],
      childBranchIds: [],
      businessUnitIds: [],
      childEntities: [],
      parentEntities: [],
      selectedRepPeriod: [],
      nullInfoConfig: [],
    });
  };

  const handleBackToSummary = () => {
    setSimplifiedEditForm(false);
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  };

  const handleDeleteSuccess = () => {
    // Refresh the list after deletion
    fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
  };

  // options.stayOnPage: when true (e.g. save from Delete confirmation), stay on edit page and do not call fetchAnalyticsFilters/setHasSummaryData
  const handleSave = (savedTitle, savedConfigRows, savedFilter, options = {}) => {
    if (!savedTitle.trim() || !savedConfigRows || savedConfigRows.length === 0) {
      return;
    }
    const stayOnPage = options.stayOnPage === true;

    const { metricIds, datapointIds } = extractMetricAndDatapointIds(savedConfigRows);
    const { equations, equationConfig } = extractEquations(savedConfigRows);
    const payload = {
      standardId: savedFilter.standardId || glbRegulationId,
      periodType: savedFilter.periodType || 'YEARLY',
      period: savedFilter.period || null,
      category: savedFilter.categoryType ? CompareCategoryType[savedFilter.categoryType] : 'ENVIRONMENTAL',
      topicIds: savedFilter.topicIds || [],
      metricIds: metricIds,
      datapointIds: datapointIds,
      equations: equations,
      equationConfig: equationConfig,
      parentBranchIds: savedFilter.parentEntities || [],
      childBranchIds: savedFilter.childEntities || [],
      businessUnitIds: [...(savedFilter.parentEntities || []), ...(savedFilter.childEntities || [])],
      childEntities: savedFilter.childEntities || [],
      parentEntities: savedFilter.parentEntities || [],
      selectedRepPeriod: savedFilter.selectedRepPeriod || [],
      configRows: savedConfigRows.map((row, index) => {
        // Build level1 object - include equation if equation checkbox is enabled
        let level1Obj = row.level1;
        if (row.useLevel1Equation && row.level1Equation && row.level1Equation.expressionWithId) {
          const expWithId = row.level1Equation.expressionWithId;
          const exp = row.level1Equation.expression;
          // If level1 is null (equation-only), create a minimal object with just the equation
          if (!level1Obj) {
            level1Obj = {
              eqExpressionWithId: expWithId,
              eqExpression: exp,
              itemType: 'equation'
            };
          } else {
            level1Obj = {
              ...level1Obj,
              eqExpressionWithId: expWithId,
              eqExpression: exp,
              itemType: 'equation'
            };
          }
        }

        // Build level2 array - include all rows: metrics, datapoints, and equations
        const level2 = row.level2Rows && row.level2Rows.length > 0
          ? [
            // Include rows with metrics
            ...row.level2Rows.filter(r => r.metric).map(r => {
              const item = {
                ...r.metric,
                itemType: 'metric',
                label: r.label || '',
                drillDownEnabled: r.drillDownEnabled || false,
                drillDownType: r.drillDownType || 'equation'
              };
              // If the selected metric carries its own equation, persist it for drilldown
              if (r.metric?.eqExpressionWithId) {
                item.eqExpressionWithId = r.metric.eqExpressionWithId;
                item.eqExpression = r.metric.eqExpression || null;
              }
              if (r.useEquation && r.equation && r.equation.expressionWithId) {
                item.eqExpressionWithId = r.equation.expressionWithId;
                item.eqExpression = r.equation.expression;
              }
              return item;
            }),
            // Include rows with datapoints
            ...row.level2Rows.filter(r => r.datapoint).map(r => {
              const item = {
                ...r.datapoint,
                itemType: 'datapoint',
                label: r.label || '',
                drillDownEnabled: r.drillDownEnabled || false,
                drillDownType: r.drillDownType || 'equation'
              };
              if (r.useEquation && r.equation && r.equation.expressionWithId) {
                item.eqExpressionWithId = r.equation.expressionWithId;
                item.eqExpression = r.equation.expression;
              }
              return item;
            }),
            // Include rows with ONLY equations (no metric or datapoint)
            ...row.level2Rows.filter(r => r.useEquation && r.equation && r.equation.expressionWithId && !r.metric && !r.datapoint).map(r => {
              return {
                eqExpressionWithId: r.equation.expressionWithId,
                eqExpression: r.equation.expression,
                label: r.label || '',
                itemType: 'equation', // Mark as equation type
                drillDownEnabled: r.drillDownEnabled || false,
                drillDownType: r.drillDownType || 'equation'
              };
            })
          ]
          : row.level2 || [];

        return {
          id: row.id,
          name: row.name,
          level1: level1Obj,
          level2: level2,
          showPercentage: row.showPercentage !== false,
          sortOrder: index + 1,
        };
      }),
      filterName: savedTitle.trim(),
      filterId: filterId || null,
    };

    setLoaderDisplay(true);
    CompanyDashboardService.saveAnalyticsFilter(payload)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status === 200 || res.status === 201) {
          if (res.data && res.data.id) {
            setFilterId(res.data.id);
          }
          if (stayOnPage) {
            // Save was triggered from Delete confirmation: stay on edit page, do not refresh summary or switch view
          } else {
            fetchAnalyticsFilters(summaryPage, summaryItemsPerPage);
            setHasSummaryData(true);
          }
        }
      })
      .catch(err => {
        setLoaderDisplay(false);
      });
  };

  const loadFilterConfiguration = (item, mode = 'view') => {
    if (!item || !item.configData) return;

    try {
      const configData = JSON.parse(item.configData);
      if (!configData) return;

      // Create a map of equation to verticalCalcEnabled from saved equationConfig
      const equationConfigMap = new Map();
      if (configData.equationConfig && Array.isArray(configData.equationConfig)) {
        configData.equationConfig.forEach(eqConfig => {
          if (eqConfig.equation) {
            equationConfigMap.set(eqConfig.equation, eqConfig.verticalCalcEnabled || false);
          }
        });
      }

      // Transform saved configRows to match the new UI structure
      const mappedConfigRows = (configData.configRows || []).map((configRow) => {
        // Transform level1 - can be either metric or datapoint
        let level1Metric = null;
        let level1Datapoint = null;
        let useLevel1Equation = false;
        let level1Equation = null;
        let level1VerticalCalcEnabled = configRow.level1VerticalCalcEnabled || false;

        if (configRow.level1) {
          // Check if level1 has an equation property (saved when equation checkbox was enabled)
          if (configRow.level1.itemType === 'equation' && configRow.level1.eqExpressionWithId) {
            const level1EquationId = configRow.level1.eqExpressionWithId;
            useLevel1Equation = true;
            level1Equation = {
              expressionWithId: level1EquationId,
              expression: configRow.level1.eqExpression
            };
            // Get verticalCalcEnabled from equationConfig map if available
            if (equationConfigMap.has(level1EquationId)) {
              level1VerticalCalcEnabled = equationConfigMap.get(level1EquationId);
            }
            // Remove equation from level1 object to get clean metric/datapoint
            const { eqExpressionWithId, eqExpression, ...cleanLevel1 } = configRow.level1;
            if (cleanLevel1.itemType === 'metric') {
              level1Metric = cleanLevel1;
            } else if (cleanLevel1.itemType === 'datapoint') {
              level1Datapoint = cleanLevel1;
            } else {
              // Fallback: assume it's a metric if itemType is missing
              level1Metric = cleanLevel1;
            }
          } else {
            // No equation - normal metric/datapoint
            if (configRow.level1.itemType === 'metric') {
              level1Metric = configRow.level1;
            } else if (configRow.level1.itemType === 'datapoint') {
              level1Datapoint = configRow.level1;
            } else {
              // Fallback: assume it's a metric if itemType is missing
              level1Metric = configRow.level1;
            }
          }
        }

        // Transform level2 - check for new level2Rows structure first, then fallback to old structure
        let level2Rows = [];
        const level2Metrics = [];
        const level2Datapoints = [];

        // Create a map of level2 items by label to match equations
        const level2ItemMap = new Map();
        if (configRow.level2 && Array.isArray(configRow.level2)) {
          configRow.level2.forEach(item => {
            const key = item.label || `${item.itemType}_${item.id}`;
            level2ItemMap.set(key, item);
          });
        }

        if (configRow.level2Rows && Array.isArray(configRow.level2Rows)) {
          // New structure with level2Rows - match with level2 array to get equations
          level2Rows = configRow.level2Rows.map(row => {
            const rowData = {
              id: row.id || Date.now() + Math.random(),
              label: row.label || '',
              metric: row.metric || null,
              datapoint: row.datapoint || null,
              useEquation: false,
              equation: null,
              verticalCalcEnabled: row.verticalCalcEnabled || false,
              drillDownEnabled: row.drillDownEnabled || false,
              drillDownType: row.drillDownType || 'equation',
              metricSearchTerm: '',
              datapointSearchTerm: '',
              showMetricDropdown: false,
              showDatapointDropdown: false
            };
            // Check if corresponding level2 item has equation property
            const key = row.label || `${row.metric ? 'metric' : 'datapoint'}_${row.metric?.id || row.datapoint?.id}`;
            const level2Item = level2ItemMap.get(key);
            // Only mark as equation if the saved item itself is of type 'equation'
            if (level2Item && level2Item.itemType === 'equation' && level2Item.eqExpressionWithId) {
              const level2EquationId = level2Item.eqExpressionWithId;
              rowData.useEquation = true;
              rowData.equation = {
                expressionWithId: level2EquationId,
                expression: level2Item.eqExpression
              };
              // Get verticalCalcEnabled from equationConfig map if available
              if (equationConfigMap.has(level2EquationId)) {
                rowData.verticalCalcEnabled = equationConfigMap.get(level2EquationId);
              }
            }
            return rowData;
          });
          level2Rows.forEach(row => {
            if (row.metric) level2Metrics.push(row.metric);
            if (row.datapoint) level2Datapoints.push(row.datapoint);
          });
        } else if (configRow.level2 && Array.isArray(configRow.level2)) {
          // Legacy structure - convert to new level2Rows format
          configRow.level2.forEach(item => {
            const row = {
              id: Date.now() + Math.random(),
              label: item.label || '',
              metric: null,
              datapoint: null,
              useEquation: false,
              equation: null,
              verticalCalcEnabled: item.verticalCalcEnabled || false,
              drillDownEnabled: item.drillDownEnabled || false,
              drillDownType: item.drillDownType || 'equation',
              metricSearchTerm: '',
              datapointSearchTerm: '',
              showMetricDropdown: false,
              showDatapointDropdown: false
            };
            // Check if item has equation property (saved when equation checkbox was enabled)
            if (item.itemType === 'equation' && item.eqExpressionWithId) {
              const level2EquationId = item.eqExpressionWithId;
              row.useEquation = true;
              row.equation = {
                expressionWithId: level2EquationId,
                expression: item.eqExpression
              };
              // Get verticalCalcEnabled from equationConfig map if available
              if (equationConfigMap.has(level2EquationId)) {
                row.verticalCalcEnabled = equationConfigMap.get(level2EquationId);
              }
              // Remove equation from item to get clean metric/datapoint
              const { eqExpressionWithId, eqExpression, ...cleanItem } = item;
              if (cleanItem.itemType === 'metric') {
                row.metric = cleanItem;
                level2Metrics.push(cleanItem);
              } else if (cleanItem.itemType === 'datapoint') {
                row.datapoint = cleanItem;
                level2Datapoints.push(cleanItem);
              }
            } else {
              // No equation - normal metric/datapoint
              if (item.itemType === 'metric') {
                row.metric = item;
                level2Metrics.push(item);
              } else if (item.itemType === 'datapoint') {
                row.datapoint = item;
                level2Datapoints.push(item);
              }
            }
            level2Rows.push(row);
          });
        }

        const sortOrderRaw = Number(configRow.sortOrder);
        const sortOrderSaved = Number.isFinite(sortOrderRaw) ? sortOrderRaw : 0;
        return {
          id: configRow.id || Date.now() + Math.random(),
          name: configRow.name || '',
          // New UI structure
          level1Metric,
          level1Datapoint,
          level2Rows,
          level2Metrics,
          level2Datapoints,
          // Legacy structure (keep for API compatibility)
          level1: configRow.level1,
          level2: configRow.level2,
          // Equation state
          useLevel1Equation,
          level1Equation,
          level1VerticalCalcEnabled,
          // UI state
          level1MetricSearchTerm: level1Metric ? (level1Metric.name || level1Metric.code) : '',
          level1DatapointSearchTerm: level1Datapoint ? (level1Datapoint.name || level1Datapoint.code) : '',
          showLevel1MetricDropdown: false,
          showLevel1DatapointDropdown: false,
          // Percentage column visibility
          showPercentage: configRow.showPercentage !== false,
          sortOrder: sortOrderSaved,
        };
      });

      const transformedConfigRows = [...mappedConfigRows].sort((a, b) => {
        return Number(a.sortOrder) - Number(b.sortOrder);
      }).map((row, i) => ({
        ...row,
        sortOrder: i + 1,
      }));

      // Populate filter state from saved configuration
      // Handle both old and new data structures
      const parentEntities = configData.parentEntities || configData.parentBranchIds || [];
      const childEntities = configData.childEntities || configData.childBranchIds || [];

      // Convert category string to categoryType if needed
      let categoryType = configData.categoryType;
      if (!categoryType && configData.category) {
        // Convert category string to categoryType numeric value
        const categoryMap = {
          'ENVIRONMENTAL': DataViewCategoryType.ENVIRONMENTAL,
          'SOCIAL': DataViewCategoryType.SOCIAL,
          'GOVERNANCE': DataViewCategoryType.GOVERNANCE
        };
        categoryType = categoryMap[configData.category] || DataViewCategoryType.ENVIRONMENTAL;
      }
      categoryType = categoryType || DataViewCategoryType.ENVIRONMENTAL;

      // Reconstruct selectedRepPeriod from period object if not already present
      let selectedRepPeriod = configData.selectedRepPeriod || [];
      if (!selectedRepPeriod || selectedRepPeriod.length === 0) {
        const periodObj = configData.period;
        const periodType = configData.periodType || 'YEARLY';

        if (periodObj && typeof periodObj === 'object') {
          selectedRepPeriod = [];

          if (periodType === 'YEARLY') {
            // For YEARLY: period = { "2024": [], "2025": [] }
            Object.keys(periodObj).forEach(year => {
              selectedRepPeriod.push({
                value: parseInt(year),
                category: 'Years'
              });
            });
          } else if (periodType === 'QUARTERLY') {
            // For QUARTERLY: period = { "2024": ["Q1", "Q2"], "2025": ["Q3"] }
            Object.keys(periodObj).forEach(year => {
              const quarters = periodObj[year];
              if (Array.isArray(quarters)) {
                quarters.forEach(quarter => {
                  selectedRepPeriod.push({
                    value: quarter,
                    category: parseInt(year)
                  });
                });
              }
            });
          } else if (periodType === 'MONTHLY') {
            // For MONTHLY: period = { "2024": ["JAN", "FEB"], "2025": ["MAR"] }
            Object.keys(periodObj).forEach(year => {
              const months = periodObj[year];
              if (Array.isArray(months)) {
                months.forEach(month => {
                  selectedRepPeriod.push({
                    value: month,
                    category: parseInt(year)
                  });
                });
              }
            });
          }
        }
      }

      const savedFilter = {
        standardId: configData.standardId || glbRegulationId,
        periodType: configData.periodType || 'YEARLY',
        period: configData.period || null,
        category: configData.category || 'ENVIRONMENTAL',
        categoryType: categoryType,
        topicIds: configData.topicIds || [],
        metricIds: configData.metricIds || [],
        datapointIds: configData.datapointIds || [],
        parentBranchIds: parentEntities,
        childBranchIds: childEntities,
        businessUnitIds: [...parentEntities, ...childEntities],
        childEntities: childEntities,
        parentEntities: parentEntities,
        selectedRepPeriod: selectedRepPeriod,
      };

      // Set the filter, configRows, and title
      // Use filterName from new structure, fallback to title for old structure
      setFilter(savedFilter);
      setConfigRows(transformedConfigRows);
      setTitle(configData.filterName || configData.title || '');

      // Set edit mode and filter ID
      setEditMode(mode === 'edit');
      setFilterId(item.id);

      // Switch to filter view
      setHasSummaryData(false);
      setSummaryData(null);

      // Apply filters to load data
      if (transformedConfigRows.length > 0 && savedFilter.period) {
        const { metricIds, datapointIds } = extractMetricAndDatapointIds(transformedConfigRows);
        // Use saved equationConfig if available, otherwise extract from configRows
        let equationConfig = configData.equationConfig || [];
        let equations = configData.equations || [];

        // If equationConfig is not available, extract from configRows
        if (!equationConfig || equationConfig.length === 0) {
          const extracted = extractEquations(transformedConfigRows);
          equations = extracted.equations;
          equationConfig = extracted.equationConfig;
        }

        const updatedFilter = {
          ...savedFilter,
          metricIds,
          datapointIds,
          equations,
          equationConfig
        };
        applyFilter(updatedFilter);
        getGraphData(updatedFilter, metricIds, datapointIds, equations, equationConfig);
      }
    } catch (error) {
    }
  };

  const handleCardClick = (item) => {
    loadFilterConfiguration(item, 'view');
  };

  const handleViewClick = (item) => {
    loadFilterConfiguration(item, 'view');
  };

  const handleEditClick = (item) => {
    setSimplifiedEditForm(true); // Edit button: show only + button in config form
    loadFilterConfiguration(item, 'edit');
  };

  // When hasSummaryData is false and there's no filterId, we're creating a new configuration
  // So default to edit mode
  useEffect(() => {
    if (!hasSummaryData && !filterId) {
      setEditMode(true);
    }
  }, [hasSummaryData, filterId]);

  const getDataViewData = (f) => {
    setLoaderDisplay(true);
    CompanyDashboardService.getMetricDVAnalytics(f)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status !== 200) return;
        setDataViewData(res.data);
        processFilteredData(res.data);
      }).catch(err => {
        setLoaderDisplay(false);
      });
  };

  const getGraphData = (f, metricIds, datapointIds, equations = [], equationConfig = []) => {
    const periodType = f.periodType || 'YEARLY';
    const allMonths = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    // Map quarters to months
    const quarterToMonths = {
      'Q1': ['JAN', 'FEB', 'MAR'],
      'Q2': ['APR', 'MAY', 'JUN'],
      'Q3': ['JUL', 'AUG', 'SEP'],
      'Q4': ['OCT', 'NOV', 'DEC']
    };

    // Extract years and selected periods based on periodType
    const graphPeriod = {};

    if (f.selectedRepPeriod && Array.isArray(f.selectedRepPeriod) && f.selectedRepPeriod.length > 0) {
      if (periodType === 'YEARLY') {
        // For YEARLY: period.value is the year
        f.selectedRepPeriod.forEach(period => {
          const year = period.value ? String(period.value) : null;
          if (year && !graphPeriod[year]) {
            graphPeriod[year] = [...allMonths]; // All 12 months for selected years
          }
        });
      } else if (periodType === 'QUARTERLY') {
        // For QUARTERLY: period.category is the year, period.value is the quarter (Q1, Q2, Q3, Q4)
        f.selectedRepPeriod.forEach(period => {
          const year = period.category ? String(period.category) : null;
          const quarter = period.value;

          if (year && quarter && quarterToMonths[quarter]) {
            if (!graphPeriod[year]) {
              graphPeriod[year] = [];
            }
            // Add months for this quarter (avoid duplicates)
            quarterToMonths[quarter].forEach(month => {
              if (!graphPeriod[year].includes(month)) {
                graphPeriod[year].push(month);
              }
            });
          }
        });
      } else if (periodType === 'MONTHLY') {
        // For MONTHLY: period.category is the year, period.value is the month (JAN, FEB, etc.)
        f.selectedRepPeriod.forEach(period => {
          const year = period.category ? String(period.category) : null;
          const month = period.value;

          if (year && month && allMonths.includes(month)) {
            if (!graphPeriod[year]) {
              graphPeriod[year] = [];
            }
            // Add only the selected month (avoid duplicates)
            if (!graphPeriod[year].includes(month)) {
              graphPeriod[year].push(month);
            }
          }
        });
      }
    } else if (f.period && typeof f.period === 'object') {
      // Fallback: extract from period object
      if (periodType === 'YEARLY') {
        // For YEARLY: period = { "2024": [], "2025": [] }
        Object.keys(f.period).forEach(year => {
          graphPeriod[year] = [...allMonths];
        });
      } else if (periodType === 'QUARTERLY') {
        // For QUARTERLY: period = { "2024": ["Q1", "Q2"], "2025": ["Q3"] }
        Object.keys(f.period).forEach(year => {
          const quarters = f.period[year];
          if (Array.isArray(quarters)) {
            graphPeriod[year] = [];
            quarters.forEach(quarter => {
              if (quarterToMonths[quarter]) {
                quarterToMonths[quarter].forEach(month => {
                  if (!graphPeriod[year].includes(month)) {
                    graphPeriod[year].push(month);
                  }
                });
              }
            });
          }
        });
      } else if (periodType === 'MONTHLY') {
        // For MONTHLY: period = { "2024": ["JAN", "FEB"], "2025": ["MAR"] }
        Object.keys(f.period).forEach(year => {
          const months = f.period[year];
          if (Array.isArray(months)) {
            graphPeriod[year] = months.filter(month => allMonths.includes(month));
          }
        });
      }
    }

    // Check if we have any periods
    if (Object.keys(graphPeriod).length === 0) {
      setGraphData(null);
      return;
    }

    // Create graph filter with modified payload
    const graphFilter = {
      standardId: f.standardId || glbRegulationId,
      periodType: 'MONTHLY',
      period: graphPeriod,
      category: f.categoryType ? CompareCategoryType[f.categoryType] : 'ENVIRONMENTAL',
      topicIds: f.topicIds || [],
      metricIds: metricIds || [],
      datapointIds: datapointIds || [],
      equations: equations || [],
      equationConfig: equationConfig || []
    };
    setGraphData(null);
  };

  const applyFilter = (f) => {
    if (!f || typeof f !== 'object') return;
    // Resolve categoryType: prefer numeric f.categoryType (0|1|2), else derive from f.category string
    const categoryMap = {
      'ENVIRONMENTAL': DataViewCategoryType.ENVIRONMENTAL,
      'SOCIAL': DataViewCategoryType.SOCIAL,
      'GOVERNANCE': DataViewCategoryType.GOVERNANCE
    };
    let validCategoryType = DataViewCategoryType.ENVIRONMENTAL;
    if (f.categoryType != null) {
      const n = Number(f.categoryType);
      if (!Number.isNaN(n) && n >= 0 && n <= 2) validCategoryType = n;
    } else if (f.category) {
      const key = String(f.category).trim().toUpperCase();
      if (categoryMap[key] !== undefined) validCategoryType = categoryMap[key];
    }
    const compareFilter = {
      standardId: f.standardId || glbRegulationId,
      periodType: f.periodType,
      period: f.period,
      category: CompareCategoryType[validCategoryType] || 'ENVIRONMENTAL',
      categoryType: validCategoryType,
      topicIds: f.topicIds || [],
      metricIds: f.metricIds || [],
      datapointIds: f.datapointIds || [],
      equations: f.equations || [],
      equationConfig: f.equationConfig || [],
      parentBranchIds: f.parentEntities || [],
      childBranchIds: f.childEntities || [],
      businessUnitIds: [...(f.parentEntities || []), ...(f.childEntities || [])],
      childEntities: f.childEntities || [],
      parentEntities: f.parentEntities || [],
      selectedRepPeriod: f.selectedRepPeriod || [],
      nullInfoConfig: f.nullInfoConfig || [],
    };
    setFilter(compareFilter);
    setAppliedFilter(compareFilter);
    sortOrder = compareFilter.businessUnitIds;
    const p = {
      ...compareFilter,
    };
    getDataViewData(p);
  };

  const processFilteredData = (data) => {
    let yearData = [];
    const branchesSet = new Map();

    if (data) {
      for (const [parentKey, patentValue] of Object.entries(data)) {
        if (patentValue) {
          for (const [childKey, childValue] of Object.entries(patentValue)) {
            yearData.push(parentKey + (childKey != 'YEARLY' ? '-' + childKey : ''));

            // New structure: childValue is an object with metrics and datapoints arrays
            if (childValue && typeof childValue === 'object' && !Array.isArray(childValue)) {
              const { metrics = [], datapoints = [], equations = [] } = childValue;

              // Collect branches from metrics
              metrics.forEach(metric => {
                if (metric.branchConsolidated && Array.isArray(metric.branchConsolidated)) {
                  metric.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });

              // Collect branches from datapoints
              datapoints.forEach(datapoint => {
                if (datapoint.branchConsolidated && Array.isArray(datapoint.branchConsolidated)) {
                  datapoint.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });

              equations.forEach(equation => {
                if (equation.branchConsolidated && Array.isArray(equation.branchConsolidated)) {
                  equation.branchConsolidated.forEach(branch => {
                    if (branch.branchId && !branchesSet.has(branch.branchId)) {
                      branchesSet.set(branch.branchId, {
                        branchId: branch.branchId,
                        branchName: branch.branchName,
                        parentBranchId: branch.parentBranchId
                      });
                    }
                  });
                }
              });
            }
          }
        }
      }
      setYearData(yearData);
      setBranches(Array.from(branchesSet.values()));
    }
  };

  const handleFilterChange = (newFilters) => {
    // Just update the filter state, don't fetch data yet
    const compareFilter = {
      standardId: newFilters.standardId || glbRegulationId,
      periodType: newFilters.periodType || 'YEARLY',
      period: newFilters.period,
      category: newFilters.categoryType ? CompareCategoryType[newFilters.categoryType] : 'ENVIRONMENTAL',
      categoryType: newFilters.categoryType || DataViewCategoryType.ENVIRONMENTAL,
      topicIds: newFilters.topicIds || [],
      metricIds: newFilters.metricIds || [],
      datapointIds: newFilters.datapointIds || [],
      parentBranchIds: newFilters.parentEntities || [],
      childBranchIds: newFilters.childEntities || [],
      businessUnitIds: [...(newFilters.parentEntities || []), ...(newFilters.childEntities || [])],
      childEntities: newFilters.childEntities || [],
      parentEntities: newFilters.parentEntities || [],
      selectedRepPeriod: newFilters.selectedRepPeriod || [],
    };
    setFilter(compareFilter);
  };

  const extractMetricAndDatapointIds = (configRows) => {
    const metricIds = [];
    const datapointIds = [];

    configRows.forEach(config => {
      // Level1 can now be either a metric or a datapoint - check itemType
      if (config.level1 && config.level1.id) {
        if (config.level1.itemType === 'metric') {
          // This is a metric - add to metricIds
          const metricId = config.level1.id;
          if (!metricIds.includes(metricId)) {
            metricIds.push(metricId);
          }
        } else if (config.level1.itemType === 'datapoint') {
          // This is a datapoint - add to datapointIds
          const datapointId = config.level1.id;
          if (!datapointIds.includes(datapointId)) {
            datapointIds.push(datapointId);
          }
        }
        // If itemType is missing, fallback to treating as metric for backward compatibility
        else if (!config.level1.itemType) {
          const metricId = config.level1.id;
          if (!metricIds.includes(metricId)) {
            metricIds.push(metricId);
          }
        }
      }

      // Level2 can contain both metrics and datapoints
      // We need to separate them based on itemType
      if (config.level2 && Array.isArray(config.level2)) {
        config.level2.forEach(item => {
          if (!item || !item.id) return;

          // Strictly check itemType - only add to the correct array
          if (item.itemType === 'metric') {
            // This is a metric - add to metricIds only
            const metricId = item.id;
            if (!metricIds.includes(metricId)) {
              metricIds.push(metricId);
            }
          } else if (item.itemType === 'datapoint') {
            // This is a datapoint - add to datapointIds only
            const datapointId = item.id;
            if (!datapointIds.includes(datapointId)) {
              datapointIds.push(datapointId);
            }
          }
          // If itemType is missing or unknown, skip it (don't add to either array)
        });
      }
    });

    return {
      metricIds: [...new Set(metricIds)],
      datapointIds: [...new Set(datapointIds)]
    };
  };

  // NOTE: When called from handleApplyFilters, pass { strict: true } to ensure
  // equations are included ONLY for actual equation-type rows.
  const extractEquations = (configRows, options = {}) => {
    const { strict = false } = options;
    const equations = [];
    const equationConfig = [];

    configRows.forEach(config => {
      // Level1 — equation id lives on `level1.eqExpressionWithId`
      const l1 = config.level1;
      const includeLevel1Eq = strict
        ? l1?.itemType === 'equation' && l1?.eqExpressionWithId
        : l1?.eqExpressionWithId;
      if (includeLevel1Eq) {
        const equation = l1.eqExpressionWithId;
        if (equation && !equations.includes(equation)) {
          equations.push(equation);
          equationConfig.push({
            equation: equation,
            verticalCalcEnabled: l1.verticalCalcEnabled || false
          });
        }
      }

      // Level2
      if (config.level2Rows && Array.isArray(config.level2Rows)) {
        config.level2Rows.forEach(row => {
          const includeLevel2Eq = strict
            ? (row.useEquation && !row.metric && !row.datapoint && row.equation && row.equation.expressionWithId)
            : (row.useEquation && row.equation && row.equation.expressionWithId);
          if (includeLevel2Eq) {
            const equation = row.equation.expressionWithId;
            if (equation && !equations.includes(equation)) {
              equations.push(equation);
              equationConfig.push({
                equation: equation,
                verticalCalcEnabled: row.verticalCalcEnabled || false
              });
            }
          }
        });
      }
    });

    return { equations, equationConfig };
  };

  const stackedBarChartCarouselItems = useMemo(
    () => collectStackedBarCarouselItemsFromDataView(dataViewData, configRows),
    [dataViewData, configRows]
  );

  const [chartsCarouselIndex, setChartsCarouselIndex] = useState(0);

  useEffect(() => {
    setChartsCarouselIndex(0);
  }, [stackedBarChartCarouselItems]);

  const handleApplyFilters = async (configRowsFromHeader) => {
    if (!filter.period || (filter.period && Object.keys(filter.period).length === 0)) {
      return;
    }
    const rowsToUse = configRowsFromHeader || configRows;
    if (!rowsToUse || rowsToUse.length === 0) {
      return;
    }
    const { metricIds, datapointIds } = extractMetricAndDatapointIds(rowsToUse);
    // Use strict extraction ONLY here
    const { equations, equationConfig } = extractEquations(rowsToUse, { strict: true });
    const nullInfoConfig = buildNullInfoConfig(rowsToUse);
    const updatedFilter = {
      ...filter,
      metricIds: metricIds,
      datapointIds: datapointIds,
      equations: equations,
      equationConfig: equationConfig,
      nullInfoConfig: nullInfoConfig,
      // Add filterId when in edit mode
      ...(editMode && filterId ? { filterId: filterId } : {})
    };
    applyFilter(updatedFilter);

    // Fetch graph data separately with modified payload
    getGraphData(updatedFilter, metricIds, datapointIds, equations, equationConfig);
  };



  const buildNullInfoConfig = (rows) => {
    const nullInfoConfig = [];
    const seen = new Set();

    (rows || []).forEach((config) => {
      const level1 = config?.level1;
      const level1Equation = level1?.eqExpressionWithId;
      if (level1?.itemType === 'metric' && level1.id > 0) {
        const key = `metric:${level1.id}:${level1Equation}`;
        if (!seen.has(key)) {
          seen.add(key);
          nullInfoConfig.push({
            id: level1.id,
            type: 'metric',
            equation: level1Equation
          });
        }
        return;
      }
      if (level1?.itemType === 'equation') {
        const key = `equation:${level1Equation}`;
        if (!seen.has(key)) {
          seen.add(key);
          nullInfoConfig.push({
            type: 'equation',
            equation: level1Equation
          });
        }
      }
    });

    return nullInfoConfig;
  };

  return (
    <div className={`ghg-emission-data-management ${!hasSummaryData && !editMode ? 'esg-analytics-view-mode' : ''}`}>

      {hasSummaryData ? (
        <ESGAnalyticsSummaryCard
          t={t}
          summaryData={summaryData}
          onNewClick={handleNewClick}
          onDeleteSuccess={handleDeleteSuccess}
          onCardClick={handleCardClick}
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          title={t("ESGAnalytics.unitWiseComparisonSettings.label") || "Unit wise comparison settings"}
          newButtonLabel={t("ESGAnalytics.addWidgets.label") || "Add Widgets"}
          pagination={{
            itemsTotalCount: summaryTotalCount,
            itemsPerPage: summaryItemsPerPage,
            currentPage: summaryPage,
            setCurrentPage: setSummaryPage,
            setItemsPerPage: setSummaryItemsPerPage,
            onPageChange: handleSummaryPageChange,
          }}
        />
      ) : (
        <div className={`${!editMode ? 'esg-analytics-view-layout' : ''} ${isScrolled && !editMode ? 'esg-analytics-scrolled' : ''}`}>
          {/* {!editMode && (
            <Button
              variant="outline-secondary"
              onClick={handleBackToSummary}
              className="d-flex align-items-center gap-2 esg-analytics-back-button"
            >
              <BiArrowBack size={18} />
              {t("Back.label") || "Back"}
            </Button>
          )} */}
          <div className="List_header mb-2">
            <h5 className='MH'>{t("Widgets.label") || "Widgets"}</h5>
            <div>
              <Button
                variant="outline-secondary"
                onClick={handleBackToSummary}
                className="d-flex align-items-center gap-2 esg-analytics-back-button"
              >
                <BiArrowBack size={18} />
                {t("Back.label") || "Back"}
              </Button>
            </div>
          </div>
          <div className='esg-analytics-header-container 3'>
            <ESGUnitWiseConfigForm
              t={t}
              onFilterChange={handleFilterChange}
              filter={filter}
              onApplyFilters={handleApplyFilters}
              onConfigRowsChange={setConfigRows}
              initialConfigRows={configRows}
              editMode={editMode}
              onSave={handleSave}
              title={title}
              onTitleChange={setTitle}
              simplifiedEditForm={simplifiedEditForm}
              showValidationOnForm={showValidationOnForm}
            />
            <div className={!editMode ? 'esg-analytics-content-area pt-0 1' : ''}>
              {title && !editMode && (
                <div className='d-flex align-items-center gap-3 esg-analytics-back-header'>
                  <h5 className='text-primary mb-0'>{title}</h5>
                </div>
              )}
              {!editMode && (
                <>
                  {stackedBarChartCarouselItems.length > 0 ? (
                    <Carousel
                      activeIndex={chartsCarouselIndex}
                      onSelect={(index) => setChartsCarouselIndex(index)}
                      className="esg-analytics-charts-carousel"
                      prevIcon={<BiChevronLeft size={28} aria-hidden />}
                      nextIcon={<BiChevronRight size={28} aria-hidden />}
                      prevLabel={t('ESGAnalytics.previousCharts.label') || 'Previous charts'}
                      nextLabel={t('ESGAnalytics.nextCharts.label') || 'Next charts'}
                      indicators={stackedBarChartCarouselItems.length > 1}
                      controls={stackedBarChartCarouselItems.length > 1}
                      interval={null}
                    >
                      {stackedBarChartCarouselItems.map((item) => {
                        const carouselConfigRow = getConfigRowForStackedBarCarouselItem(
                          item,
                          configRows
                        );
                        return (
                          <Carousel.Item
                            key={item.itemKind === 'equation' ? `eq-${item.equationId}` : `m-${item.metricId}`}
                            className="esg-analytics-charts-carousel-slide"
                          >
                            <ESGAnalyticsStackedBarChart
                              t={t}
                              dataViewData={dataViewData}
                              configRows={[carouselConfigRow]}
                              allConfigRows={configRows}
                              filter={appliedFilter ?? filter}
                              branches={branches}
                              yearData={yearData}
                              showChartMenu={true}
                              chartTitle={carouselConfigRow?.name}
                            />
                          </Carousel.Item>
                        );
                      })}
                    </Carousel>
                  ) : (
                    <ESGAnalyticsStackedBarChart
                      t={t}
                      dataViewData={dataViewData}
                      configRows={configRows}
                      allConfigRows={configRows}
                      filter={appliedFilter ?? filter}
                      branches={branches}
                      yearData={yearData}
                      showChartMenu={true}
                    />
                  )}
                  <ESGAnalyticsLineGraphYearly
                    t={t}
                    dataViewData={dataViewData}
                    configRows={configRows}
                    filter={appliedFilter ?? filter}
                    showChartMenu={true}
                    showMaximizeInMenu={false}
                    showViewTableInMenu={false}
                  />
                  <ESGAnalyticsCards
                    t={t}
                    configRows={configRows}
                    dataViewData={dataViewData}
                    filter={appliedFilter ?? filter}
                    branches={branches}
                    dataPoints={dataPoints}
                    yearData={yearData}
                    section="second"
                  />
                  <ESGAnalyticsCards
                    t={t}
                    configRows={configRows}
                    dataViewData={dataViewData}
                    filter={appliedFilter ?? filter}
                    branches={branches}
                    dataPoints={dataPoints}
                    yearData={yearData}
                    section="first"
                  />
                </>
              )}
              {/* <ESGAnalyticsLineGraph
                t={t}
                graphData={graphData}
                configRows={configRows}
                showChartMenu={true}
                showMaximizeInMenu={false}
                showViewTableInMenu={false}
              /> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(ESGAnalytics);
